/* External */
import {
  useRef, useEffect, memo,
} from 'react';
import PropTypes from 'prop-types';

/* Utils */
import { useScrollIntoView, ScrollPosition, ScrollMode } from '../ScrollView';
import withCursorVisibilityFilterFocusable from '../../hocs/withCursorVisibilityFilter.hoc';

function ScrollIntoViewIfFocused({
  position = ScrollPosition.START,
  mode = undefined,
  focused = false,
  hasFocusedChild = false,
  className = undefined,
  style = undefined,
  children,
  cursorVisibilityChanged = false,
  assignRef = () => {},
  dataPosition = undefined,
  scrollEnabled = true,
}) {
  const cursorVisibilityChangedRef = useRef(cursorVisibilityChanged);
  const ref = useRef();
  const scrollIntoView = useScrollIntoView();

  cursorVisibilityChangedRef.current = cursorVisibilityChanged;

  useEffect(() => {
    if (scrollEnabled && (hasFocusedChild || focused) && ref?.current) {
      const modeToUse = mode
        ?? (cursorVisibilityChangedRef.current ? ScrollMode.IF_NEEDED : ScrollMode.ALWAYS);
      scrollIntoView(ref.current, {
        position,
        mode: modeToUse,
      });
    }
  }, [focused, hasFocusedChild, mode, position, scrollEnabled, scrollIntoView]);

  useEffect(() => {
    assignRef(ref.current);
  });

  return (
    <div
      className={className}
      ref={ref}
      style={style}
      data-carousel-pos={dataPosition}
    >
      {children}
    </div>
  );
}

ScrollIntoViewIfFocused.propTypes = {
  focused: PropTypes.bool,
  hasFocusedChild: PropTypes.bool,
  cursorVisibilityChanged: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.objectOf(PropTypes.string),
  children: PropTypes.node.isRequired,
  assignRef: PropTypes.func,
  mode: PropTypes.oneOf([
    ScrollMode.ALWAYS,
    ScrollMode.IF_NEEDED,
  ]),
  position: PropTypes.oneOf([
    ScrollPosition.START,
    ScrollPosition.END,
    ScrollPosition.CENTER,
  ]),
  dataPosition: PropTypes.number,
  scrollEnabled: PropTypes.bool,
};

export default memo(
  withCursorVisibilityFilterFocusable({
    trackChildren: true,
    autoRestoreFocus: false,
    forgetLastFocusedChild: true,
  })(
    memo(ScrollIntoViewIfFocused),
  ),
);
